import { useState, useLayoutEffect, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import PageModal from '../common/PageModal'
import SimpleButton from '../common/SimpleButton'
import { toast } from '../common/Toast'
import UserSearchInput from '../common/UserSearchInput'
import { loadCompanyRoles, putCompany, clearCompanyRoles } from '../../actions/companies'
import { changeBranchAdmin } from '../../actions/branches'
import { companiesWritePermission } from '../../utils/permissionValidation'
import './change-admin-modal.scss'

const ChangeAdminModal = (props) => {
  const { t } = useTranslation()
  const { selectedCompany, selectedBranch, companyRoles, showSpinner, setShowSpinner, closeModal } = props
  const [selectedRoleId, setSelectedRoleId] = useState(null)
  const [roleIds, setRoleIds] = useState([])
  const [newAdminUser, setNewAdminUser] = useState(null)
  const [haveError, setHaveError] = useState({})
  const basicDentistRole = 'Dentist'
  const basicDealerRole = 'Dealer Technician'

  useLayoutEffect(() => {
    if (!selectedCompany?.id && !selectedBranch?.companyId) {
      return
    }

    props.actions.loadCompanyRoles(selectedCompany?.id || selectedBranch?.companyId)
  }, [])

  useEffect(() => {
    if (companyRoles.length > 0) {
      setRoleIds(companyRoles.map((role) => role.id))
      setSelectedRoleId(companyRoles.find((c) => c.name === basicDentistRole || c.name === basicDealerRole)?.id)
    }
  }, [companyRoles])

  const footerContent = () => {
    return (
      <>
        <SimpleButton className="cancel-button" onClick={() => handleCloseModal()}>
          {t('cancel')}
        </SimpleButton>
        <SimpleButton
          className="submit-button"
          onClick={() => handleChangeAdmin()}
          disabled={showSpinner || hasSameInput()}
          requiredPermission={companiesWritePermission}
        >
          {t('save')}
        </SimpleButton>
      </>
    )
  }

  const handleCloseModal = () => {
    props.actions.clearCompanyRoles()
    closeModal()
  }

  const hasSameInput = () => {
    return selectedCompany?.email === newAdminUser || selectedBranch?.email === newAdminUser
  }

  const isValidInput = () => {
    let errors = {}
    let isValid = true

    if (!selectedRoleId) {
      errors = { ...errors, role: true }
      isValid = false
    }

    if (!newAdminUser) {
      errors = { ...errors, newAdmin: true }
      isValid = false
    }

    setHaveError(errors)
    return isValid
  }

  const handleChangeAdmin = () => {
    if (!isValidInput()) {
      toast.error(t('error.fill_all_fields'))
      return
    }

    if (selectedCompany) {
      const bodyParams = {
        companyName: selectedCompany.companyName,
        country: selectedCompany.country,
        officeAddressOne: selectedCompany.officeAddressOne,
        city: selectedCompany.city,
        stateProvinceRegion: selectedCompany.stateProvinceRegion,
        zipPostalCode: selectedCompany.zipPostalCode,
        status: selectedCompany.status,
        prevAdminRoleId: selectedRoleId,
        email: newAdminUser,
      }

      props.actions.putCompany(selectedCompany.id, bodyParams).then(() => {
        handleCloseModal()
      })
    }

    if (selectedBranch) {
      const bodyParams = {
        email: newAdminUser,
        prevAdminRoleId: selectedRoleId,
      }

      props.actions.changeBranchAdmin(selectedBranch.companyId, selectedBranch.id, bodyParams).then(() => {
        handleCloseModal()
      })
    }
  }

  return (
    <>
      <PageModal
        toggle
        onToggle={() => handleCloseModal()}
        className="change-company-admin-modal"
        title={t('organization_context.change_admin')}
        footerContent={footerContent()}
      >
        <div className="change-company-admin-modal-content">
          <div className="row-wrapper">
            <div className="disclaimer">
              <p>{selectedCompany ? t('organization_context.change_admin_disclaimer') : t('organization_context.change_branch_admin_disclaimer')}</p>
            </div>
          </div>
          <div className="row-wrapper">
            <div className="input-wrapper">
              <p className="input-option">{t('organization_context.current_admin')}</p>
              <input type="text" className="name-input" value={selectedCompany?.email || selectedBranch?.email || ''} disabled />
            </div>
          </div>
          <div className="row-wrapper">
            <div className="users-wrapper input-wrapper">
              <div className="input-option">{t('organization_context.new_admin')}*</div>
              <UserSearchInput
                initialSelectedKey={newAdminUser}
                onChange={(user) => setNewAdminUser(user.key)}
                hasError={haveError['newAdmin']}
                defaultQueryParams={{ roleId: roleIds, status: 'activated' }}
                localFilters={{ branchName: selectedCompany?.branches[0]?.branchName || selectedBranch?.branchName }}
                setShowSpinner={setShowSpinner}
              />
            </div>
          </div>
        </div>
      </PageModal>
    </>
  )
}

function stateToProps({ companies }) {
  return {
    companyRoles: companies?.roles || [],
  }
}

function dispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        putCompany,
        changeBranchAdmin,
        loadCompanyRoles,
        clearCompanyRoles,
      },
      dispatch,
    ),
  }
}

export default connect(stateToProps, dispatchToProps)(ChangeAdminModal)
